/**
 * AdminOrderDetail.jsx — Page centrale de la commande entreprise
 *
 * Vue consolidée :
 *  - Fiche commande (devis lié, budget, statut, calendrier)
 *  - Panier atomique (total / alloué / restant / consommé)
 *  - Gestion collaborateurs : import Excel/CSV avec preview, ajout manuel,
 *    update quota, suspension, suppression, bar de progression quota.
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';
import { toast } from 'sonner';
import {
  ArrowLeft, ShoppingCart, Upload, Users, FileText, AlertTriangle,
  CheckCircle2, Loader2, Plus, Trash2, Pause, Play, UserCheck,
} from 'lucide-react';
import AdminOrderConsumptionInvoicing from './AdminOrderConsumptionInvoicing';
import OrderInsightsPanel from './OrderInsightsPanel';

const API = getApiUrl();

const ORDER_STATUS = {
  pending_setup: { label: 'Configuration', color: '#D97706', bg: '#FEF3C7' },
  active: { label: 'Active', color: '#047857', bg: '#D1FAE5' },
  completed: { label: 'Terminée', color: '#065F46', bg: '#A7F3D0' },
  cancelled: { label: 'Annulée', color: '#991B1B', bg: '#FEE2E2' },
};

const COLL_STATUS = {
  invited: { label: 'Invité', color: '#92400E', bg: '#FEF3C7' },
  active: { label: 'Actif', color: '#047857', bg: '#D1FAE5' },
  suspended: { label: 'Suspendu', color: '#991B1B', bg: '#FEE2E2' },
  exhausted: { label: 'Quota épuisé', color: '#6B7280', bg: '#F3F4F6' },
};

export default function AdminOrderDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/enterprise/orders/${id}`, { headers });
      if (!res.ok) throw new Error('Commande introuvable');
      setData(await res.json());
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, [id]); // eslint-disable-line

  const uploadPreview = async (file) => {
    setPreviewing(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/admin/enterprise/orders/${id}/collaborators/preview`, {
        method: 'POST', body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Erreur'); }
      setPreview(await res.json());
    } catch (e) { toast.error(e.message); } finally { setPreviewing(false); }
  };

  const confirmImport = async () => {
    if (!preview || !preview.can_proceed) return;
    const rows = preview.rows.filter(r => r.valid).map(r => ({
      email: r.email, first_name: r.first_name, last_name: r.last_name,
      sessions_allocated: r.sessions_allocated,
    }));
    setImporting(true);
    try {
      const res = await fetch(`${API}/admin/enterprise/orders/${id}/collaborators/import`, {
        method: 'POST', headers, body: JSON.stringify({ rows }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Erreur'); }
      toast.success(`${rows.length} collaborateurs importés`);
      setPreview(null); fetchData();
    } catch (e) { toast.error(e.message); } finally { setImporting(false); }
  };

  const updateQuota = async (collabId, newQuota) => {
    await fetch(`${API}/admin/enterprise/collaborators/${collabId}/quota`, {
      method: 'PUT', headers, body: JSON.stringify({ sessions_allocated: newQuota }),
    }).then(r => r.ok ? toast.success('Quota mis à jour') : r.json().then(e => toast.error(e.detail)));
    fetchData();
  };
  const updateStatus = async (collabId, status) => {
    await fetch(`${API}/admin/enterprise/collaborators/${collabId}/status`, {
      method: 'PUT', headers, body: JSON.stringify({ status }),
    });
    fetchData();
  };
  const deleteCollab = async (collabId) => {
    if (!window.confirm('Supprimer ce collaborateur ? Son quota sera restitué au panier.')) return;
    const res = await fetch(`${API}/admin/enterprise/collaborators/${collabId}`, {
      method: 'DELETE', headers,
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); toast.error(e.detail); return; }
    toast.success('Supprimé'); fetchData();
  };

  if (loading || !data) return <div className="p-10 text-[#6B7280]">Chargement…</div>;

  const { order, basket, collaborators } = data;
  const status = ORDER_STATUS[order.status] || {};
  const snap = order.company_snapshot || {};
  const allocatedPct = basket ? (basket.allocated_sessions / basket.total_sessions) * 100 : 0;
  const consumedPct = basket ? (basket.consumed_sessions / basket.total_sessions) * 100 : 0;

  return (
    <div className="theme-light min-h-screen bg-[#FBF9F3] pb-20">
      <div className="border-b border-[#E8E5DE] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <button onClick={() => navigate(-1)} data-testid="admin-order-back"
            className="inline-flex items-center gap-1.5 text-[#6B7280] hover:text-[#111827] text-sm mb-4">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="uppercase text-[11px] tracking-[0.25em] text-[#92710A] font-medium">
                Commande · {order.type === 'ia' ? 'Coaching IA' : 'Coaching Humain'}
              </p>
              <h1 className="text-2xl sm:text-3xl font-display font-medium text-[#111827] mt-1">{snap.company_name}</h1>
              <p className="text-sm text-[#6B7280] mt-1 font-mono">Devis {order.quote_number}</p>
            </div>
            <span className="inline-flex items-center text-[11px] uppercase tracking-wider font-medium px-3 py-1.5 rounded-full"
              style={{ color: status.color, backgroundColor: status.bg }}
              data-testid="admin-order-status-badge">{status.label}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left main */}
        <div className="lg:col-span-2 space-y-4">
          {/* Basket */}
          {basket && (
            <section className="bg-white border border-[#E8E5DE] rounded-2xl p-5" data-testid="admin-order-basket">
              <h2 className="text-sm font-display font-medium text-[#111827] mb-3 flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-[#92710A]" /> Panier de sessions
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <Stat label="Total acheté" value={basket.total_sessions} />
                <Stat label="Alloué" value={basket.allocated_sessions} accent="#047857" />
                <Stat label="Restant" value={basket.remaining_sessions} accent="#92710A" />
                <Stat label="Consommé" value={basket.consumed_sessions} accent="#1E40AF" />
              </div>
              <div className="relative h-3 rounded-full bg-[#F3F4F6] overflow-hidden">
                <div className="absolute top-0 left-0 h-full bg-[#A7F3D0]"
                  style={{ width: `${allocatedPct}%` }} />
                <div className="absolute top-0 left-0 h-full bg-[#047857]"
                  style={{ width: `${consumedPct}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-[#6B7280] mt-1.5">
                <span>{consumedPct.toFixed(0)}% consommé</span>
                <span>{allocatedPct.toFixed(0)}% alloué</span>
              </div>
            </section>
          )}

          {/* Collaborators */}
          <section className="bg-white border border-[#E8E5DE] rounded-2xl p-5" data-testid="admin-order-collaborators">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="text-sm font-display font-medium text-[#111827] flex items-center gap-2">
                <Users className="w-4 h-4 text-[#92710A]" /> Collaborateurs ({collaborators.length})
              </h2>
              <div className="flex gap-2">
                <button onClick={() => setShowAdd(s => !s)}
                  data-testid="admin-order-add-collab-toggle"
                  className="px-3 py-1.5 rounded-full bg-[#F8F7F4] border border-[#E8E5DE] text-[#4B5563] text-xs font-medium hover:bg-[#EBEAE3] inline-flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" /> Ajout manuel
                </button>
                <label className="px-3 py-1.5 rounded-full bg-[#111827] text-white text-xs font-medium hover:bg-[#1f2937] cursor-pointer inline-flex items-center gap-1.5">
                  <Upload className="w-3.5 h-3.5" /> Import Excel/CSV
                  <input type="file" className="hidden" accept=".xlsx,.csv"
                    data-testid="admin-order-upload-input"
                    onChange={(e) => e.target.files?.[0] && uploadPreview(e.target.files[0])} />
                </label>
              </div>
            </div>

            {showAdd && <ManualAddForm orderId={id} token={token} onDone={() => { setShowAdd(false); fetchData(); }} />}

            {previewing && <div className="text-[13px] text-[#6B7280] py-3"><Loader2 className="w-4 h-4 animate-spin inline" /> Analyse du fichier…</div>}

            {preview && <ImportPreview preview={preview} onConfirm={confirmImport} onCancel={() => setPreview(null)} importing={importing} />}

            {collaborators.length === 0 ? (
              <div className="text-sm text-[#6B7280] py-6 text-center bg-[#FBF9F3] border border-[#E8DFC8] rounded-xl">
                Aucun collaborateur. Ajoutez-en manuellement ou importez un fichier.
              </div>
            ) : (
              <div className="space-y-2" data-testid="admin-order-collab-list">
                {collaborators.map(c => (
                  <CollaboratorRow key={c.id} c={c} onQuota={updateQuota} onStatus={updateStatus} onDelete={deleteCollab} />
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Right sidebar */}
        <aside className="space-y-4">
          <section className="bg-white border border-[#E8E5DE] rounded-2xl p-5">
            <h2 className="text-sm font-display font-medium text-[#111827] mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#92710A]" /> Contrat
            </h2>
            <div className="space-y-2 text-sm">
              <KV label="Devis" value={order.quote_number} />
              <KV label="Type" value={order.type === 'ia' ? 'Coaching IA' : 'Coaching Humain'} />
              <KV label="Mode facturation" value={order.billing_mode === 'forfait' ? 'Forfait' : 'À la consommation'} />
              <KV label="Budget HT" value={`${order.total_budget_ht.toLocaleString('fr-FR')} €`} />
              <KV label="Budget TTC" value={`${order.total_budget_ttc.toLocaleString('fr-FR')} €`} />
              <KV label="Durée" value={`${order.duration_months} mois`} />
              <KV label="Début" value={new Date(order.start_date).toLocaleDateString('fr-FR')} />
              <KV label="Fin prévue" value={new Date(order.end_date).toLocaleDateString('fr-FR')} />
            </div>
            <Link to={`/admin/enterprise-quotes/${order.quote_request_id}`}
              className="mt-3 inline-flex items-center gap-1 text-xs text-[#92710A] hover:underline">
              <ArrowLeft className="w-3 h-3" /> Revenir au dossier commercial
            </Link>
          </section>
          <section className="bg-white border border-[#E8E5DE] rounded-2xl p-5" data-testid="admin-order-shortlist">
            <h2 className="text-sm font-display font-medium text-[#111827] mb-3 flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-[#92710A]" /> Appel d'offre coach
            </h2>
            <CoachShortlistSection orderId={id} token={token} />
          </section>
        </aside>
      </div>

      {/* 🎁 Sprint P0 — Prestation COACHDIA Prism (individuel + groupe) */}
      <div className="max-w-7xl mx-auto px-6 pb-4">
        <OrderInsightsPanel orderId={id} order={order} token={token} />
      </div>

      {/* Vague 5 — Consommation & Facturation (pleine largeur) */}
      <div className="max-w-7xl mx-auto px-6 pb-8">
        <AdminOrderConsumptionInvoicing orderId={id} order={order} token={token} />
      </div>
    </div>
  );
}

function Stat({ label, value, accent = '#111827' }) {
  return (
    <div className="text-center p-3 rounded-xl bg-[#FBF9F3] border border-[#E8DFC8]">
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">{label}</div>
      <div className="text-2xl font-display mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}
function KV({ label, value }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-[#F0EEE8] last:border-0">
      <span className="text-xs text-[#6B7280]">{label}</span>
      <span className="text-sm text-[#111827] text-right">{value}</span>
    </div>
  );
}

function ImportPreview({ preview, onConfirm, onCancel, importing }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="mb-4 p-4 rounded-xl bg-[#FBF9F3] border border-[#E8DFC8]" data-testid="admin-import-preview">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-sm">
          <span className="font-medium text-[#111827]">{preview.valid_count}/{preview.total}</span>
          <span className="text-[#6B7280]"> lignes valides</span>
          <span className="text-[#6B7280] mx-2">·</span>
          <span className="text-[#4B5563]">Quotas cumulés demandés : </span>
          <span className="font-medium text-[#92710A]">{preview.total_quota_requested}</span>
          <span className="text-[#6B7280]"> / restant : {preview.remaining_in_basket}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={onCancel} className="px-3 py-1.5 rounded-full border border-[#E8E5DE] bg-white text-[#4B5563] text-xs font-medium">Annuler</button>
          <button onClick={onConfirm} disabled={!preview.can_proceed || importing}
            data-testid="admin-import-confirm"
            className="px-4 py-1.5 rounded-full bg-[#059669] text-white text-xs font-medium hover:bg-[#047857] disabled:opacity-50 inline-flex items-center gap-1.5">
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Importer les {preview.valid_count} valides
          </button>
        </div>
      </div>
      {preview.summary_errors.length > 0 && (
        <div className="mb-3 p-2 rounded-lg bg-[#FEE2E2] border border-[#FCA5A5] text-[12px] text-[#991B1B]">
          <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
          {preview.summary_errors.join(' · ')}
        </div>
      )}
      <div className="max-h-60 overflow-y-auto border border-[#E8DFC8] rounded-lg bg-white">
        <table className="w-full text-[12px]">
          <thead className="bg-[#F8F7F4] sticky top-0">
            <tr className="text-[#6B7280]">
              <th className="p-2 text-left">#</th>
              <th className="p-2 text-left">Email</th>
              <th className="p-2 text-left">Prénom</th>
              <th className="p-2 text-left">Nom</th>
              <th className="p-2 text-right">Quota</th>
              <th className="p-2 text-left">Erreurs</th>
            </tr>
          </thead>
          <tbody>
            {preview.rows.map(r => (
              <tr key={r.row_number} className={r.valid ? 'bg-white' : 'bg-[#FEF2F2]'}>
                <td className="p-2 text-[#6B7280]">{r.row_number}</td>
                <td className="p-2">{r.email || '—'}</td>
                <td className="p-2">{r.first_name || '—'}</td>
                <td className="p-2">{r.last_name || '—'}</td>
                <td className="p-2 text-right">{r.sessions_allocated ?? '—'}</td>
                <td className="p-2 text-[#991B1B]">{r.errors.join(' · ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}

function ManualAddForm({ orderId, token, onDone }) {
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', sessions_allocated: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/enterprise/orders/${orderId}/collaborators`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const msg = body?.detail
          ? (Array.isArray(body.detail) ? body.detail.map(d => d.msg || d).join(' · ') : String(body.detail))
          : `Erreur (${res.status})`;
        throw new Error(msg);
      }
      toast.success('Collaborateur ajouté'); onDone();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit}
      className="mb-4 p-4 rounded-xl bg-[#FBF9F3] border border-[#E8DFC8] space-y-2"
      data-testid="admin-order-add-form">
      <input required type="email" placeholder="email@exemple.com" value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        data-testid="add-collab-email"
        className="w-full p-2.5 rounded-lg bg-white border border-[#E8E5DE] text-sm focus:outline-none focus:border-[#92710A]" />
      <div className="grid grid-cols-2 gap-2">
        <input required placeholder="Prénom" value={form.first_name}
          onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          data-testid="add-collab-first"
          className="w-full p-2.5 rounded-lg bg-white border border-[#E8E5DE] text-sm focus:outline-none focus:border-[#92710A]" />
        <input required placeholder="Nom" value={form.last_name}
          onChange={(e) => setForm({ ...form, last_name: e.target.value })}
          data-testid="add-collab-last"
          className="w-full p-2.5 rounded-lg bg-white border border-[#E8E5DE] text-sm focus:outline-none focus:border-[#92710A]" />
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <label className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium block mb-1">Quota sessions</label>
          <input required type="number" min="0" placeholder="Ex. 10" value={form.sessions_allocated}
            onChange={(e) => setForm({ ...form, sessions_allocated: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
            data-testid="add-collab-quota"
            className="w-full p-2.5 rounded-lg bg-white border border-[#E8E5DE] text-sm focus:outline-none focus:border-[#92710A]" />
        </div>
        <button type="submit" disabled={saving}
          data-testid="add-collab-submit"
          className="w-full sm:w-auto sm:self-end px-5 py-2.5 rounded-lg bg-[#059669] text-white text-sm font-medium hover:bg-[#047857] disabled:opacity-50 inline-flex items-center justify-center gap-1.5 whitespace-nowrap">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Ajouter le collaborateur
        </button>
      </div>
    </form>
  );
}

function CoachShortlistSection({ orderId, token }) {
  const [q, setQ] = useState('');
  const [coaches, setCoaches] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [coachRates, setCoachRates] = useState({}); // { [coach_user_id]: string }
  const [offers, setOffers] = useState([]);
  const [message, setMessage] = useState('Nous vous invitons à rejoindre cette mission entreprise.');
  const [publishing, setPublishing] = useState(false);
  const [aiMode, setAiMode] = useState(false);
  const [aiMeta, setAiMeta] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchOffers = React.useCallback(async () => {
    const res = await fetch(`${API}/admin/enterprise/orders/${orderId}/coach-offers`, { headers });
    if (res.ok) setOffers((await res.json()).items || []);
  }, [orderId, token]); // eslint-disable-line
  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  const searchCoaches = async () => {
    setAiMode(false); setAiMeta(null);
    const res = await fetch(`${API}/admin/enterprise/orders/${orderId}/shortlist/search`, {
      method: 'POST', headers, body: JSON.stringify({ q: q || null, limit: 30 }),
    });
    if (res.ok) {
      const d = await res.json();
      setCoaches((d.coaches || []).map(c => ({ ...c, _ai: null })));
    }
  };

  const runAiMatching = async () => {
    setAiLoading(true);
    try {
      const res = await fetch(`${API}/admin/enterprise/orders/${orderId}/coach-recommendations`, {
        method: 'POST', headers, body: JSON.stringify({ limit: 20 }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Recommandation IA impossible');
      }
      const data = await res.json();
      setAiMeta({
        generated_by: data.generated_by, latency_ms: data.latency_ms,
        total_candidates: data.total_candidates,
      });
      // Les recos sont déjà triées par score desc
      const mapped = (data.recommendations || []).map(r => ({
        ...r.coach_snapshot, _ai: { score: r.score, reasons: r.reasons },
        _history: r.history || null,
        existing_offer: r.existing_offer,
      }));
      setCoaches(mapped);
      setAiMode(true);
      toast.success(
        data.generated_by === 'fallback_rule_based'
          ? `Ranking de secours (${data.total_candidates} coachs) — IA indisponible`
          : `🪄 IA a classé ${data.total_candidates} coachs en ${Math.round((data.latency_ms || 0) / 1000)}s${data.coaches_with_history ? ` · ${data.coaches_with_history} avec historique` : ''}`
      );
    } catch (e) { toast.error(e.message); } finally { setAiLoading(false); }
  };

  const toggle = (uid) => {
    const s = new Set(selected);
    s.has(uid) ? s.delete(uid) : s.add(uid);
    setSelected(s);
    // Pré-remplir le tarif coach avec le hourly_rate de la fiche coach si coché
    if (!coachRates[uid]) {
      const c = coaches.find(x => x.user_id === uid);
      if (c && c.hourly_rate) {
        setCoachRates(prev => ({ ...prev, [uid]: String(c.hourly_rate) }));
      }
    }
  };

  const setRateFor = (uid, value) => {
    setCoachRates(prev => ({ ...prev, [uid]: value }));
  };

  const publish = async () => {
    if (selected.size === 0) return;
    // Validation : chaque coach coché DOIT avoir un tarif > 0
    const items = [];
    const missing = [];
    for (const uid of selected) {
      const raw = coachRates[uid];
      const rate = parseFloat(raw);
      if (!raw || isNaN(rate) || rate <= 0) {
        missing.push(uid);
      } else {
        items.push({ coach_user_id: uid, coach_rate_eur: rate });
      }
    }
    if (missing.length > 0) {
      toast.error(`Tarif coach manquant ou invalide pour ${missing.length} coach(s)`);
      return;
    }
    setPublishing(true);
    try {
      const res = await fetch(`${API}/admin/enterprise/orders/${orderId}/coach-offers/publish`, {
        method: 'POST', headers,
        body: JSON.stringify({ items, personal_message: message, ttl_days: 7 }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(typeof e.detail === 'string' ? e.detail : 'Erreur'); }
      const d = await res.json();
      const emailsNote = d.emails_sent_count != null
        ? ` · ${d.emails_sent_count}/${d.created_count} email(s) envoyé(s)`
        : '';
      toast.success(`${d.created_count} offre(s) publiée(s)${emailsNote}`);
      setSelected(new Set());
      setCoachRates({});
      fetchOffers();
    } catch (e) { toast.error(e.message); } finally { setPublishing(false); }
  };

  const counts = offers.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2 text-center text-[11px]">
        <div className="p-2 rounded-lg bg-[#FEF3C7] text-[#92400E]"><div className="font-display text-lg">{counts.pending || 0}</div>en attente</div>
        <div className="p-2 rounded-lg bg-[#D1FAE5] text-[#047857]"><div className="font-display text-lg">{counts.accepted || 0}</div>acceptées</div>
        <div className="p-2 rounded-lg bg-[#FEE2E2] text-[#991B1B]"><div className="font-display text-lg">{counts.declined || 0}</div>refusées</div>
        <div className="p-2 rounded-lg bg-[#F3F4F6] text-[#6B7280]"><div className="font-display text-lg">{counts.expired || 0}</div>expirées</div>
      </div>
      <button onClick={runAiMatching} disabled={aiLoading}
        data-testid="shortlist-ai-match-btn"
        className="w-full px-3 py-2.5 rounded-lg bg-gradient-to-r from-[#92710A] to-[#B8860B] text-white text-xs font-semibold hover:opacity-95 disabled:opacity-50 inline-flex items-center justify-center gap-2">
        {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <span>🪄</span>}
        {aiLoading ? 'IA en train d\'analyser le brief…' : 'Suggérer des coachs par IA'}
      </button>
      {aiMeta && aiMode && (
        <div className="text-[10px] text-[#6B7280] -mt-1.5">
          {aiMeta.generated_by === 'fallback_rule_based'
            ? 'Ranking déterministe (IA indisponible)'
            : `Analysé par GPT-5.2 · ${aiMeta.total_candidates} candidats · ${Math.round((aiMeta.latency_ms || 0))}ms`}
        </div>
      )}
      <div className="flex gap-2 items-center">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Recherche manuelle (nom, spécialité…)"
          data-testid="shortlist-search-q"
          className="flex-1 p-2 rounded-lg bg-white border border-[#E8E5DE] text-sm" />
        <button onClick={searchCoaches} data-testid="shortlist-search-btn"
          className="px-3 py-2 rounded-lg bg-[#111827] text-white text-xs whitespace-nowrap">Chercher</button>
      </div>
      {coaches.length > 0 && (
        <div className="max-h-[60vh] overflow-y-auto space-y-2" data-testid="shortlist-coaches">
          {coaches.map(c => {
            const offer = c.existing_offer;
            const disabled = offer && offer.status === 'pending';
            const isSelected = selected.has(c.user_id);
            const ai = c._ai;
            const scoreColor = !ai ? '#6B7280' :
              ai.score >= 80 ? '#047857' : ai.score >= 60 ? '#92710A' : '#991B1B';
            const scoreBg = !ai ? '#F3F4F6' :
              ai.score >= 80 ? '#D1FAE5' : ai.score >= 60 ? '#FEF3C7' : '#FEE2E2';
            const initials = (c.display_name || c.user_id || '?')
              .split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
            return (
              <div key={c.user_id}
                onClick={() => !disabled && toggle(c.user_id)}
                data-testid={`shortlist-coach-card-${c.user_id}`}
                className={`relative rounded-xl border transition-all ${
                  isSelected ? 'border-[#059669] bg-[#ECFDF5] ring-2 ring-[#059669]/20' : 'border-[#E8E5DE] bg-white hover:border-[#92710A]'
                } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                <div className="p-3">
                  {/* HEADER — avatar + identité + score */}
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {c.avatar_url ? (
                        <img src={c.avatar_url} alt={c.display_name}
                          className="w-11 h-11 rounded-full object-cover border border-[#E8DFC8]" />
                      ) : (
                        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#92710A] flex items-center justify-center text-white text-sm font-semibold">
                          {initials}
                        </div>
                      )}
                    </div>
                    {/* Identité */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[#111827] font-display font-medium text-sm truncate"
                          data-testid={`coach-name-${c.user_id}`}>
                          {c.display_name || c.user_id}
                        </span>
                        {c.rating && (
                          <span className="text-[11px] text-[#92710A] font-medium whitespace-nowrap">
                            ★ {c.rating}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {c.hourly_rate && (
                          <span className="text-[11px] text-[#111827] font-medium">
                            {c.hourly_rate} €/h
                          </span>
                        )}
                        {c.total_sessions != null && (
                          <span className="text-[11px] text-[#6B7280]">
                            · {c.total_sessions} sess.
                          </span>
                        )}
                        {c.level && (
                          <span className="text-[10px] uppercase text-[#4B5563] bg-[#FBF9F3] border border-[#E8DFC8] px-1.5 py-0.5 rounded-full">
                            {c.level}
                          </span>
                        )}
                        {offer && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${offer.status === 'accepted' ? 'bg-[#D1FAE5] text-[#047857]' : offer.status === 'pending' ? 'bg-[#FEF3C7] text-[#92400E]' : 'bg-[#F3F4F6] text-[#6B7280]'}`}>
                            {offer.status}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Badge score + case à cocher */}
                    <div className="flex-shrink-0 flex flex-col items-center gap-1.5">
                      {ai && (
                        <div className="flex flex-col items-center justify-center px-2 py-1 rounded-lg"
                          style={{ backgroundColor: scoreBg, color: scoreColor, minWidth: 44 }}>
                          <div className="text-[15px] font-display font-bold leading-none">{ai.score}</div>
                          <div className="text-[8px] uppercase tracking-wider mt-0.5">score</div>
                        </div>
                      )}
                      <input type="checkbox" checked={isSelected}
                        onChange={(e) => { e.stopPropagation(); !disabled && toggle(c.user_id); }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={disabled}
                        data-testid={`shortlist-coach-${c.user_id}`}
                        className="w-5 h-5 accent-[#059669] cursor-pointer" />
                    </div>
                  </div>

                  {/* Spécialités — max 4, tronquées visuellement */}
                  {(c.specialties || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(c.specialties || []).slice(0, 4).map((s, i) => (
                        <span key={i}
                          className="text-[10px] text-[#4B5563] bg-[#F8F7F4] border border-[#E8DFC8] px-1.5 py-0.5 rounded-full max-w-[140px] truncate">
                          {s}
                        </span>
                      ))}
                      {(c.specialties || []).length > 4 && (
                        <span className="text-[10px] text-[#6B7280]">
                          +{c.specialties.length - 4}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Raisons IA — compactes et hiérarchisées */}
                  {ai && ai.reasons && ai.reasons.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-[#F0EEE8]">
                      <div className="text-[9px] uppercase tracking-wider text-[#92710A] font-semibold mb-1">
                        Justification IA
                      </div>
                      <ul className="space-y-0.5">
                        {ai.reasons.slice(0, 4).map((r, i) => (
                          <li key={i}
                            className="text-[11px] text-[#374151] flex items-start gap-1.5 leading-tight"
                            data-testid={`ai-reason-${c.user_id}-${i}`}>
                            <span className="text-[#047857] flex-shrink-0 mt-0.5">✓</span>
                            <span>{r}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Historique — compact */}
                  {c._history && c._history.has_history && (
                    <div className="mt-2 pt-2 border-t border-[#F0EEE8] flex flex-wrap gap-1"
                      data-testid={`coach-history-${c.user_id}`}>
                      {c._history.legacy_bookings_completed > 0 && (
                        <span className="text-[10px] text-[#1E40AF] bg-[#DBEAFE] border border-[#BFDBFE] px-1.5 py-0.5 rounded-full">
                          📊 {c._history.legacy_bookings_completed} sess.
                        </span>
                      )}
                      {c._history.legacy_rating_avg != null && (
                        <span className="text-[10px] text-[#92710A] bg-[#FEF3C7] border border-[#FDE68A] px-1.5 py-0.5 rounded-full">
                          ★ {c._history.legacy_rating_avg} ({c._history.legacy_rating_count})
                        </span>
                      )}
                      {c._history.enterprise_missions_count > 0 && (
                        <span className="text-[10px] text-[#065F46] bg-[#A7F3D0] border border-[#6EE7B7] px-1.5 py-0.5 rounded-full">
                          🏢 {c._history.enterprise_missions_count} B2B
                        </span>
                      )}
                      {c._history.enterprise_dispute_rate != null && c._history.enterprise_dispute_rate > 0.1 && (
                        <span className="text-[10px] text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-1.5 py-0.5 rounded-full">
                          ⚠ {Math.round(c._history.enterprise_dispute_rate * 100)}%
                        </span>
                      )}
                    </div>
                  )}

                  {/* 💰 Tarif coach (visible dès sélection) */}
                  {isSelected && !disabled && (
                    <div className="mt-2 pt-2 border-t border-[#E8DFC8]"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`shortlist-rate-wrap-${c.user_id}`}>
                      <label className="text-[10px] uppercase tracking-wider text-[#92710A] font-semibold block mb-1">
                        Tarif coach (€/session net) *
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number" min="1" step="0.5"
                          value={coachRates[c.user_id] ?? ''}
                          onChange={(e) => setRateFor(c.user_id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="ex. 120"
                          data-testid={`shortlist-coach-rate-${c.user_id}`}
                          className="flex-1 p-1.5 rounded-md bg-white border border-[#E8DFC8] text-[12px] font-medium focus:outline-none focus:border-[#92710A]" />
                        <span className="text-[11px] text-[#4B5563] whitespace-nowrap">€/session</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Barre d'action sticky : toujours visible dès qu'il y a des coachs */}
      {coaches.length > 0 && (
        <div className="sticky bottom-0 -mx-3 px-3 pt-2 pb-3 bg-gradient-to-t from-white via-white to-white/80 border-t border-[#E8E5DE]"
          data-testid="shortlist-action-bar">
          <div className="text-[11px] text-[#4B5563] mb-1.5 flex items-center justify-between">
            <span>
              <b className="text-[#111827]">{selected.size}</b> / {coaches.length} coach(s) sélectionné(s)
            </span>
            {selected.size > 0 && (
              <button onClick={(e) => { e.stopPropagation(); setSelected(new Set()); }}
                className="text-[10px] text-[#6B7280] hover:text-[#111827] underline">
                Tout décocher
              </button>
            )}
          </div>
          {selected.size > 0 && (
            <textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Message d'accompagnement pour les coachs sélectionnés…"
              data-testid="shortlist-message"
              className="w-full mb-2 p-2 rounded-lg bg-white border border-[#E8E5DE] text-[12px]" />
          )}
          <button onClick={publish}
            disabled={publishing || selected.size === 0 || Array.from(selected).some(uid => {
              const r = parseFloat(coachRates[uid]);
              return !coachRates[uid] || isNaN(r) || r <= 0;
            })}
            data-testid="shortlist-publish"
            className="w-full px-4 py-2.5 rounded-full bg-[#D4AF37] text-[#111827] text-sm font-semibold hover:bg-[#E5C15A] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2">
            {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : '📤'}
            {selected.size === 0
              ? 'Cochez au moins un coach pour envoyer l\'appel d\'offre'
              : `Envoyer l'appel d'offre à ${selected.size} coach(s)`}
          </button>
        </div>
      )}
    </div>
  );
}

function CollaboratorRow({ c, onQuota, onStatus, onDelete }) {
  const status = COLL_STATUS[c.status] || {};
  const [editingQuota, setEditingQuota] = useState(false);
  const [quotaValue, setQuotaValue] = useState(c.sessions_allocated);
  const usedPct = c.sessions_allocated > 0 ? (c.sessions_used / c.sessions_allocated) * 100 : 0;
  const saveQuota = () => {
    if (quotaValue !== c.sessions_allocated) onQuota(c.id, quotaValue);
    setEditingQuota(false);
  };
  return (
    <div className="p-3 rounded-xl border border-[#E8E5DE] bg-white flex items-center gap-3" data-testid={`admin-collab-row-${c.id}`}>
      <div className="w-9 h-9 rounded-full bg-[#FBF9F3] border border-[#E8DFC8] flex items-center justify-center flex-shrink-0">
        <UserCheck className="w-4 h-4 text-[#92710A]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-[#111827] truncate">{c.first_name} {c.last_name}</span>
          <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full"
            style={{ color: status.color, backgroundColor: status.bg }}>{status.label}</span>
        </div>
        <div className="text-[11px] text-[#6B7280] truncate">{c.email}</div>
        <div className="mt-1.5 flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden max-w-[200px]">
            <div className="h-full bg-[#047857]" style={{ width: `${usedPct}%` }} />
          </div>
          <span className="text-[11px] text-[#6B7280]">{c.sessions_used} / {c.sessions_allocated}</span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {editingQuota ? (
          <>
            <input type="number" value={quotaValue}
              onChange={(e) => setQuotaValue(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
              className="w-16 p-1 rounded-lg border border-[#E8E5DE] text-sm text-center" />
            <button onClick={saveQuota} className="p-1.5 rounded-lg bg-[#059669] text-white text-xs">OK</button>
            <button onClick={() => setEditingQuota(false)} className="p-1.5 rounded-lg bg-[#F3F4F6] text-[#4B5563] text-xs">×</button>
          </>
        ) : (
          <>
            <button onClick={() => setEditingQuota(true)}
              data-testid={`collab-edit-quota-${c.id}`}
              className="px-2 py-1 rounded-lg text-[10px] text-[#4B5563] hover:bg-[#F3F4F6]">Éditer quota</button>
            {c.status === 'active' || c.status === 'invited' ? (
              <button onClick={() => onStatus(c.id, 'suspended')} title="Suspendre"
                className="p-1.5 rounded-lg text-[#991B1B] hover:bg-[#FEE2E2]"><Pause className="w-3.5 h-3.5" /></button>
            ) : c.status === 'suspended' ? (
              <button onClick={() => onStatus(c.id, 'active')} title="Réactiver"
                className="p-1.5 rounded-lg text-[#047857] hover:bg-[#D1FAE5]"><Play className="w-3.5 h-3.5" /></button>
            ) : null}
            <button onClick={() => onDelete(c.id)} title="Supprimer"
              data-testid={`collab-delete-${c.id}`}
              className="p-1.5 rounded-lg text-[#991B1B] hover:bg-[#FEE2E2]"><Trash2 className="w-3.5 h-3.5" /></button>
          </>
        )}
      </div>
    </div>
  );
}
